import { ACTION_CONFIG_FIELDS } from "./actionConfigRules";

/**
 * The words for the automation codes the server stores and serves: trigger codes
 * (`automation_runner.PLATFORM_TRIGGERS`: "nps.low", "tenant.signup", ...) and action types
 * (`PLATFORM_ACTIONS`: "create_task", ...). One dictionary map each (`hauto.trigger.<code>`,
 * `hauto.action.<type>`); every Automation screen names a code through here, so a raw code never
 * reaches the operator. A code with no entry (a trigger retired after a rule stored it, a legacy
 * action that is not an object, reported as type "invalid") reads as the generic label.
 * `t` is the `superadmin` translator.
 */
const label = (t, prefix, code) =>
  (typeof code === "string" && code ? t(`${prefix}.${code}`, undefined, "") : "") || t(`${prefix}.unknown`);

export const triggerLabel = (t, code) => label(t, "hauto.trigger", code);
export const actionLabel = (t, type) => label(t, "hauto.action", type);

// The config fields an action declares (actionConfigRules.ACTION_CONFIG_FIELDS), by name.
const CONFIG_FIELD_NAMES = new Set(Object.values(ACTION_CONFIG_FIELDS).flat().map((f) => f.name));
// What `field` names when it is not a config field (automation_runner.stored_action_issue):
// the config is not an object, the type is not one the runner handles, the action is not an object.
const STRUCTURAL_ISSUES = ["config", "type", "action"];

/** The words for one config field (`hauto.cfg.field.<name>`), without "optional". */
export const configFieldLabel = (t, name) => t(`hauto.cfg.field.${name}`);

/**
 * What is wrong with one stored action, as a sentence, from the `field` the server reports for
 * it: a rule's `config_issues[].field` (hq-customers-contract.md 4.4) or a skipped run result's
 * `detail_params.field` (`invalid_config`). A config field is named by its label; `config`,
 * `type` and `action` each have their own sentence; anything else (a field this bundle does not
 * know yet) is the generic one, never the raw name.
 */
export function configIssueText(t, field) {
  if (CONFIG_FIELD_NAMES.has(field)) return t("hauto.issue.field", { field: configFieldLabel(t, field) });
  if (STRUCTURAL_ISSUES.includes(field)) return t(`hauto.issue.${field}`);
  return t("hauto.issue.other");
}
